import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Redirect, useLocation } from 'react-router-dom';
import { useCookies } from 'react-cookie';
import jwt from 'jsonwebtoken';
import styled from '@emotion/styled';

import EasyJoinRegistrationForm from '../presentational/EasyJoinRegistrationForm';
import ExistingAccountRegistrationForm from '../presentational/ExistingAccountRegistrationForm';
import ExistingPrimaryAdminRegistrationForm from '../presentational/ExistingPrimaryAdminRegistrationForm';
import { fetchUser } from '../../../login/ducks/loginActions';
import getAllUrlParams from '../../../../shared/utils/getAllUrlParams';
import getRegistrationType from '../../../../shared/utils/getRegistrationType';
import Spinner from '../../../../shared/_TBComponents/Spinner';

const SpinnerContainer = styled('div')`
  background-color: #FFFFFF;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  height: auto;
  margin-top: 250px;
  max-width: 440px;
  padding: 60px 48px;
  position: relative;
  width: 100%;
`;

const decodeToken = (token) => {
  try {
    return jwt.decode(token);
  } catch (error) {
    return null;
  }
};

const RegistrationFormContainer = () => {
  const currentUser = useSelector((state) => state.auth.data.currentUser);
  const easyJoinData = useSelector((state) => state.registration.data.easyJoinData);

  const [cookies] = useCookies(['v2token']);
  const dispatch = useDispatch();
  const location = useLocation();

  const isAuthenticated = cookies.v2token
    && cookies.v2token !== '0'
    && ((cookies.accessToken !== undefined && cookies.accessToken !== '0')
      || (cookies.refreshToken !== undefined && cookies.refreshToken !== '0'));

  const tokenData = useMemo(() => {
    const urlParams = getAllUrlParams(window.location.href, false);
    const { token } = urlParams;
    if (token) {
      return decodeToken(token);
    }
    return null;
  }, [location.search]);

  // Fetch user if authenticated but currentUser is empty
  useEffect(() => {
    if (isAuthenticated && (!currentUser || Object.keys(currentUser).length === 0)) {
      dispatch(fetchUser());
    }
  }, [dispatch, isAuthenticated, currentUser]);

  const isEasyJoin = easyJoinData && easyJoinData.easyJoinId;
  const hasCurrentUser = currentUser && Object.keys(currentUser).length > 0;
  const needsCurrentUser = !isEasyJoin;

  // Show spinner while waiting for currentUser to load
  if (needsCurrentUser && !hasCurrentUser && isAuthenticated) {
    return (
      <SpinnerContainer>
        <Spinner />
      </SpinnerContainer>
    );
  }

  // Redirect users to login if they accessed the URL directly
  // without a token, easy join data, or currentUser data
  if (!tokenData && !hasCurrentUser && !isEasyJoin) {
    return <Redirect to='/login' />;
  }

  const registrationType = getRegistrationType(currentUser, easyJoinData);

  switch (registrationType) {
    case 'EASY_JOIN':
      return (
        <EasyJoinRegistrationForm />
      );
    case 'EXISTING_PRIMARY_ADMIN':
      return (
        <ExistingPrimaryAdminRegistrationForm />
      );
    default:
      return (
        <ExistingAccountRegistrationForm tokenData={tokenData} />
      );
  }
};

export default RegistrationFormContainer;
